import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from '../components/Toast/ToastContainer';

const UpcomingReleases = () => {
    const { user, isAuthenticated } = useContext(AuthContext);
    const navigate = useNavigate();
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('newest'); // 'newest' or 'popular'
    const [showModal, setShowModal] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        releaseDate: '',
        storeLinks: [{ platform: '', url: '' }],
        trailers: ['']
    });
    const [files, setFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [selectedGame, setSelectedGame] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isEditing, setIsEditing] = useState(false);
    const [editGameId, setEditGameId] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        setPage(1);
        fetchGames(1);
    }, [sortBy]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setPage(1);
            fetchGames(1);
        }, 500);
        return () => clearTimeout(timeout);
    }, [searchTerm]);

    useEffect(() => {
        fetchGames(page);
    }, [page]);

    const fetchGames = async (currentPage = page) => {
        try {
            setLoading(true);
            const res = await axios.get('/api/upcoming-games', {
                params: {
                    search: searchTerm,
                    sort: sortBy,
                    page: currentPage
                }
            });
            setGames(res.data.data);
            setTotalPages(res.data.totalPages);
        } catch (err) {
            console.error(err);
            setError('Failed to load games');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleLinkChange = (index, field, value) => {
        const newLinks = [...formData.storeLinks];
        newLinks[index][field] = value;
        setFormData({ ...formData, storeLinks: newLinks });
    };

    const addLinkField = () => {
        setFormData({ ...formData, storeLinks: [...formData.storeLinks, { platform: '', url: '' }] });
    };

    const removeLinkField = (index) => {
        const newLinks = formData.storeLinks.filter((_, i) => i !== index);
        setFormData({ ...formData, storeLinks: newLinks });
    };

    const handleTrailerChange = (index, value) => {
        const newTrailers = [...formData.trailers];
        newTrailers[index] = value;
        setFormData({ ...formData, trailers: newTrailers });
    };

    const addTrailerField = () => {
        setFormData({ ...formData, trailers: [...formData.trailers, ''] });
    };

    const removeTrailerField = (index) => {
        const newTrailers = formData.trailers.filter((_, i) => i !== index);
        setFormData({ ...formData, trailers: newTrailers });
    };

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        if (files.length + newFiles.length > 5) {
            toast.warning('You can only upload up to 5 screenshots in total');
            return;
        }
        setFiles([...files, ...newFiles]);
    };

    const removeFile = (index) => {
        setFiles(files.filter((_, i) => i !== index));
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            releaseDate: '',
            storeLinks: [{ platform: '', url: '' }],
            trailers: ['']
        });
        setFiles([]);
        setIsEditing(false);
        setEditGameId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isAuthenticated) return navigate('/login');

        setSubmitting(true);
        const data = new FormData();
        data.append('title', formData.title);
        data.append('description', formData.description);
        data.append('releaseDate', formData.releaseDate);
        data.append('storeLinks', JSON.stringify(formData.storeLinks.filter(l => l.platform && l.url)));
        data.append('trailers', JSON.stringify(formData.trailers.filter(t => t.trim())));

        files.forEach(file => {
            data.append('media', file);
        });

        try {
            if (isEditing && editGameId) {
                await axios.put(`/api/upcoming-games/${editGameId}`, data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                await axios.post('/api/upcoming-games', data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }
            setShowModal(false);
            resetForm();
            fetchGames();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save game');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (game, e) => {
        e.stopPropagation();
        setFormData({
            title: game.title,
            description: game.description,
            releaseDate: game.releaseDate,
            storeLinks: game.storeLinks && game.storeLinks.length > 0 ? game.storeLinks : [{ platform: '', url: '' }],
            trailers: game.trailers && game.trailers.length > 0 ? game.trailers : ['']
        });
        setFiles([]); 
        setIsEditing(true);
        setEditGameId(game._id);
        setShowModal(true);
    };

    const handleDelete = async (gameId, e) => {
        e.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this game?')) return;

        try {
            await axios.delete(`/api/upcoming-games/${gameId}`);
            setGames(games.filter(g => g._id !== gameId));
            toast.success('Game deleted successfully');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete game');
        }
    };

    const handleVote = async (gameId) => {
        if (!isAuthenticated) return navigate('/login');
        try {
            const res = await axios.post(`/api/upcoming-games/${gameId}/vote`);
            setGames(games.map(g =>
                g._id === gameId ? { ...g, upvoteCount: res.data.data.upvoteCount, isUpvoted: res.data.data.isUpvoted } : g
            ));
        } catch (err) {
            console.error(err);
        }
    };

    const openGameModal = (game) => {
        setSelectedGame(game);
        setCurrentImageIndex(0);
    };

    const closeGameModal = () => {
        setSelectedGame(null);
        setCurrentImageIndex(0);
    };

    const nextImage = () => {
        if (selectedGame && selectedGame.media.length > 0) {
            setCurrentImageIndex((prev) => (prev + 1) % selectedGame.media.length);
        }
    };

    const prevImage = () => {
        if (selectedGame && selectedGame.media.length > 0) {
            setCurrentImageIndex((prev) => (prev - 1 + selectedGame.media.length) % selectedGame.media.length);
        }
    };

    return (
        <div className="container" style={{ padding: '2rem 1rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <h1 style={{ fontSize: '2.5rem', color: 'var(--text-accent)', marginBottom: '0.5rem' }}>Upcoming Releases</h1>
                <p style={{ color: 'var(--text-secondary)' }}>Discover and share games coming soon!</p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <input
                        type="text"
                        placeholder="Search games..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            padding: '0.8rem',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)',
                            background: 'var(--bg-card)',
                            color: 'var(--text-primary)',
                            minWidth: '250px'
                        }}
                    />
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        style={{
                            padding: '0.8rem',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)',
                            background: 'var(--bg-card)',
                            color: 'var(--text-primary)'
                        }}
                    >
                        <option value="newest">Newest</option>
                        <option value="popular">Most Anticipated</option>
                    </select>
                </div>

                <button
                    onClick={() => {
                        if (isAuthenticated) {
                            resetForm();
                            setShowModal(true);
                        } else {
                            navigate('/login');
                        }
                    }}
                    className="btn btn-primary"
                    style={{ borderRadius: '25px', padding: '0.8rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                    <span>+</span> Add Game
                </button>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
            ) : (
                <div className="grid">
                    {games.map(game => (
                        <div 
                            key={game._id} 
                            className="card card-hover" 
                            style={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
                            onClick={() => openGameModal(game)}
                        >
                            {game.media && game.media.length > 0 && (
                                <div style={{ height: '180px', overflow: 'hidden', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                    <img src={game.media[0].url} alt={game.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                            )}
                            <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                                    <h3 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--text-primary)' }}>{game.title}</h3>
                                    <span style={{ fontSize: '0.8rem', background: 'var(--bg-secondary)', padding: '0.2rem 0.6rem', borderRadius: '4px' }}>
                                        {game.releaseDate}
                                    </span>
                                </div>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', flex: 1, marginBottom: '1rem', whiteSpace: 'pre-line' }}>
                                    {game.description.length > 150 ? game.description.substring(0, 150) + '...' : game.description}
                                </p>

                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                                    {game.storeLinks.map((link, i) => (
                                        <a
                                            key={i}
                                            href={link.url.startsWith('http') ? link.url : `https://${link.url}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{ fontSize: '0.8rem', color: 'var(--accent-color)', textDecoration: 'none', border: '1px solid var(--accent-color)', padding: '2px 8px', borderRadius: '12px' }}
                                        >
                                            {link.platform}
                                        </a>
                                    ))}
                                </div>

                                {game.trailers && game.trailers.length > 0 && (
                                    <div style={{ marginBottom: '1rem' }}>
                                        {game.trailers.map((trailer, i) => (
                                            <a
                                                key={i}
                                                href={trailer}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-accent)', marginBottom: '0.2rem', textDecoration: 'none' }}
                                            >
                                                🎬 Watch Trailer {i + 1}
                                            </a>
                                        ))}
                                    </div>
                                )}

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        {game.userId?.profile?.avatar?.url ? (
                                            <img src={game.userId.profile.avatar.url} style={{ width: '24px', height: '24px', borderRadius: '50%' }} alt="" />
                                        ) : (
                                            <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#ccc' }}></div>
                                        )}
                                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{game.userId?.username || 'Unknown'}</span>
                                    </div>
                                    
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                        {user && game.userId && (user._id === game.userId._id || user._id === game.userId) && (
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <button
                                                    onClick={(e) => handleEdit(game, e)}
                                                    style={{
                                                        background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem',
                                                        color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', padding: '4px'
                                                    }}
                                                    title="Edit"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    onClick={(e) => handleDelete(game._id, e)}
                                                    style={{
                                                        background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem',
                                                        color: 'var(--error-color)', display: 'flex', alignItems: 'center', padding: '4px'
                                                    }}
                                                    title="Delete"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        )}

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleVote(game._id);
                                            }}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: game.isUpvoted ? 'var(--accent-color)' : 'var(--text-secondary)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.3rem',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {game.isUpvoted ? '🔥' : '🔥'} {game.upvoteCount}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '2rem' }}>
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="btn"
                        style={{
                            background: 'var(--bg-secondary)',
                            color: page === 1 ? 'var(--text-secondary)' : 'var(--text-primary)',
                            cursor: page === 1 ? 'not-allowed' : 'pointer',
                            opacity: page === 1 ? 0.5 : 1
                        }}
                    >
                        Previous
                    </button>
                    
                    <span style={{ color: 'var(--text-secondary)' }}>
                        Page {page} of {totalPages}
                    </span>

                    <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="btn"
                        style={{
                            background: 'var(--bg-secondary)',
                            color: page === totalPages ? 'var(--text-secondary)' : 'var(--text-primary)',
                            cursor: page === totalPages ? 'not-allowed' : 'pointer',
                            opacity: page === totalPages ? 0.5 : 1
                        }}
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.8)', zIndex: 1000,
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    padding: '1rem'
                }}>
                    <div style={{
                        background: 'var(--bg-card)', padding: '2rem', borderRadius: '12px',
                        width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto'
                    }}>
                        <h2 style={{ marginBottom: '1.5rem' }}>{isEditing ? 'Edit Upcoming Game' : 'Add Upcoming Game'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Game Name *</label>
                                <input
                                    name="title"
                                    required
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                                />
                            </div>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Description *</label>
                                <textarea
                                    name="description"
                                    required
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows={4}
                                    style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                                />
                            </div>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Release Date / Year *</label>
                                <input
                                    name="releaseDate"
                                    required
                                    placeholder="e.g. Q4 2025, or December 2025"
                                    value={formData.releaseDate}
                                    onChange={handleInputChange}
                                    style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                                />
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Store Links</label>
                                {formData.storeLinks.map((link, index) => (
                                    <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                        <input
                                            placeholder="Platform (Steam, Epic...)"
                                            value={link.platform}
                                            onChange={(e) => handleLinkChange(index, 'platform', e.target.value)}
                                            style={{ flex: 1, padding: '0.6rem', borderRadius: '8px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                                        />
                                        <input
                                            placeholder="URL"
                                            value={link.url}
                                            onChange={(e) => handleLinkChange(index, 'url', e.target.value)}
                                            style={{ flex: 2, padding: '0.6rem', borderRadius: '8px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                                        />
                                        <button type="button" onClick={() => removeLinkField(index)} style={{ background: 'var(--error-color)', border: 'none', borderRadius: '8px', color: 'white', padding: '0 0.8rem' }}>x</button>
                                    </div>
                                ))}
                                <button type="button" onClick={addLinkField} style={{ fontSize: '0.8rem', color: 'var(--accent-color)', background: 'none', border: 'none', cursor: 'pointer' }}>+ Add another link</button>
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem' }}>YouTube Trailers</label>
                                {formData.trailers.map((trailer, index) => (
                                    <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                        <input
                                            placeholder="YouTube Video URL"
                                            value={trailer}
                                            onChange={(e) => handleTrailerChange(index, e.target.value)}
                                            style={{ flex: 1, padding: '0.6rem', borderRadius: '8px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                                        />
                                        <button type="button" onClick={() => removeTrailerField(index)} style={{ background: 'var(--error-color)', border: 'none', borderRadius: '8px', color: 'white', padding: '0 0.8rem' }}>x</button>
                                    </div>
                                ))}
                                <button type="button" onClick={addTrailerField} style={{ fontSize: '0.8rem', color: 'var(--accent-color)', background: 'none', border: 'none', cursor: 'pointer' }}>+ Add Trailer</button>
                            </div>

                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Screenshots (Max 5)</label>
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    style={{ width: '100%', marginBottom: '0.5rem' }}
                                />
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    {files.map((file, index) => (
                                        <div key={index} style={{ background: 'var(--bg-secondary)', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span>{file.name}</span>
                                            <button type="button" onClick={() => removeFile(index)} style={{ background: 'none', border: 'none', color: 'var(--error-color)', cursor: 'pointer' }}>x</button>
                                        </div>
                                    ))}
                                </div>
                                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>{files.length} / 5 screenshots selected</p>
                            </div>

                            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    style={{ padding: '0.8rem 1.5rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn btn-primary"
                                    style={{ padding: '0.8rem 1.5rem', borderRadius: '8px', cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.7 : 1 }}
                                >
                                    {submitting ? 'Submitting...' : (isEditing ? 'Update Game' : 'Submit Game')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Game Details Modal */}
            {selectedGame && (
                <div 
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.9)', zIndex: 1000,
                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                        padding: '1rem',
                        overflowY: 'auto'
                    }}
                    onClick={closeGameModal}
                >
                    <div 
                        style={{
                            background: 'var(--bg-card)', borderRadius: '16px',
                            width: '100%', maxWidth: '900px', maxHeight: '90vh', 
                            overflowY: 'auto',
                            position: 'relative'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close Button */}
                        <button
                            onClick={closeGameModal}
                            style={{
                                position: 'absolute',
                                top: '1rem',
                                right: '1rem',
                                background: 'rgba(0,0,0,0.5)',
                                border: 'none',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                color: 'white',
                                fontSize: '1.5rem',
                                cursor: 'pointer',
                                zIndex: 10,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            ×
                        </button>

                        {/* Image Carousel */}
                        {selectedGame.media && selectedGame.media.length > 0 && (
                            <div style={{ position: 'relative', height: '400px', background: '#000', borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}>
                                <img 
                                    src={selectedGame.media[currentImageIndex].url} 
                                    alt={selectedGame.title}
                                    style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'contain'
                                    }} 
                                />
                                
                                {selectedGame.media.length > 1 && (
                                    <>
                                        {/* Previous Button */}
                                        <button
                                            onClick={prevImage}
                                            style={{
                                                position: 'absolute',
                                                left: '1rem',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                background: 'rgba(0,0,0,0.6)',
                                                border: 'none',
                                                borderRadius: '50%',
                                                width: '50px',
                                                height: '50px',
                                                color: 'white',
                                                fontSize: '1.5rem',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            ‹
                                        </button>

                                        {/* Next Button */}
                                        <button
                                            onClick={nextImage}
                                            style={{
                                                position: 'absolute',
                                                right: '1rem',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                background: 'rgba(0,0,0,0.6)',
                                                border: 'none',
                                                borderRadius: '50%',
                                                width: '50px',
                                                height: '50px',
                                                color: 'white',
                                                fontSize: '1.5rem',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            ›
                                        </button>

                                        {/* Image Counter */}
                                        <div style={{
                                            position: 'absolute',
                                            bottom: '1rem',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            background: 'rgba(0,0,0,0.7)',
                                            padding: '0.5rem 1rem',
                                            borderRadius: '20px',
                                            color: 'white',
                                            fontSize: '0.9rem'
                                        }}>
                                            {currentImageIndex + 1} / {selectedGame.media.length}
                                        </div>

                                        {/* Thumbnail Dots */}
                                        <div style={{
                                            position: 'absolute',
                                            bottom: '3rem',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            display: 'flex',
                                            gap: '0.5rem'
                                        }}>
                                            {selectedGame.media.map((_, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => setCurrentImageIndex(index)}
                                                    style={{
                                                        width: '10px',
                                                        height: '10px',
                                                        borderRadius: '50%',
                                                        border: 'none',
                                                        background: index === currentImageIndex ? 'white' : 'rgba(255,255,255,0.5)',
                                                        cursor: 'pointer',
                                                        padding: 0
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Game Details */}
                        <div style={{ padding: '2rem' }}>
                            {/* Title and Release Date */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                                <h2 style={{ margin: 0, fontSize: '2rem', color: 'var(--text-primary)' }}>
                                    {selectedGame.title}
                                </h2>
                                <span style={{ 
                                    background: 'var(--accent-color)', 
                                    color: 'white',
                                    padding: '0.5rem 1rem', 
                                    borderRadius: '8px',
                                    fontWeight: 'bold'
                                }}>
                                    📅 {selectedGame.releaseDate}
                                </span>
                            </div>

                            {/* Description */}
                            <div style={{ marginBottom: '1.5rem' }}>
                                <h3 style={{ fontSize: '1.2rem', marginBottom: '0.5rem', color: 'var(--text-accent)' }}>About</h3>
                                <p style={{ 
                                    color: 'var(--text-secondary)', 
                                    lineHeight: '1.6',
                                    whiteSpace: 'pre-line'
                                }}>
                                    {selectedGame.description}
                                </p>
                            </div>

                            {/* Store Links */}
                            {selectedGame.storeLinks && selectedGame.storeLinks.length > 0 && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <h3 style={{ fontSize: '1.2rem', marginBottom: '0.5rem', color: 'var(--text-accent)' }}>Available On</h3>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                        {selectedGame.storeLinks.map((link, i) => (
                                            <a
                                                key={i}
                                                href={link.url.startsWith('http') ? link.url : `https://${link.url}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ 
                                                    fontSize: '0.9rem', 
                                                    color: 'white',
                                                    background: 'var(--accent-color)',
                                                    textDecoration: 'none', 
                                                    padding: '0.5rem 1rem', 
                                                    borderRadius: '8px',
                                                    display: 'inline-block',
                                                    fontWeight: '500'
                                                }}
                                            >
                                                🎮 {link.platform}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Trailers */}
                            {selectedGame.trailers && selectedGame.trailers.length > 0 && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <h3 style={{ fontSize: '1.2rem', marginBottom: '0.5rem', color: 'var(--text-accent)' }}>Trailers</h3>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        {selectedGame.trailers.map((trailer, i) => (
                                            <a
                                                key={i}
                                                href={trailer}
                                                target="_blank"
                                                rel="noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ 
                                                    fontSize: '0.95rem', 
                                                    color: 'var(--accent-color)',
                                                    textDecoration: 'none',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem'
                                                }}
                                            >
                                                🎬 Watch Trailer {i + 1}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Footer: User and Vote */}
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                paddingTop: '1.5rem', 
                                borderTop: '1px solid var(--border-color)' 
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    {selectedGame.userId?.profile?.avatar?.url ? (
                                        <img 
                                            src={selectedGame.userId.profile.avatar.url} 
                                            style={{ width: '36px', height: '36px', borderRadius: '50%' }} 
                                            alt="" 
                                        />
                                    ) : (
                                        <div style={{ 
                                            width: '36px', 
                                            height: '36px', 
                                            borderRadius: '50%', 
                                            background: '#ccc',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            👤
                                        </div>
                                    )}
                                    <div>
                                        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: '500' }}>
                                            {selectedGame.userId?.username || 'Unknown'}
                                        </p>
                                        <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                            Posted {new Date(selectedGame.createdAt).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleVote(selectedGame._id);
                                    }}
                                    style={{
                                        background: selectedGame.isUpvoted ? 'var(--accent-color)' : 'transparent',
                                        border: `2px solid ${selectedGame.isUpvoted ? 'var(--accent-color)' : 'var(--border-color)'}`,
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        color: selectedGame.isUpvoted ? 'white' : 'var(--text-secondary)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        fontWeight: 'bold',
                                        padding: '0.75rem 1.5rem',
                                        fontSize: '1rem'
                                    }}
                                >
                                    🔥 {selectedGame.upvoteCount}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UpcomingReleases;
